import { ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

export enum SortDirection {
    ASC = 'asc',
    DESC = 'desc',
    NONE = 'none',
}

export type SortPayload = {
    column: string;
    order: SortDirection;
};

type UseSortOptions<S extends string> = {
    initial?: S;
    ignoreQueryParams?: boolean;
};

export function useSort<S extends string = string>(
    options: UseSortOptions<S> = {},
) {
    const sort = ref<S | undefined>(options.initial);

    const router = useRouter();
    const route = useRoute();

    function setSort(value: SortPayload) {
        if (value.order === SortDirection.NONE) {
            sort.value = undefined;
            return;
        }
        sort.value =
            value.order === SortDirection.DESC
                ? `-${value.column}`
                : value.column;
    }

    function getSortedBy(column: string): SortDirection {
        if (!sort.value) return SortDirection.NONE;
        if (sort.value === column) return SortDirection.ASC;
        if (sort.value === `-${column}`) return SortDirection.DESC;
        return SortDirection.NONE;
    }

    if (!options.ignoreQueryParams) {
        const initial = route.query?.sort as S | undefined;
        if (initial) {
            sort.value = initial;
        }

        watch(
            sort,
            (value) => {
                router?.replace({
                    query: {
                        ...route.query,
                        sort: value || undefined,
                    },
                });
            },
            { immediate: true },
        );
    }

    return {
        sort,
        setSort,
        getSortedBy,
    };
}
